// Каталог месторождений — CRUD над `/api/public/fields`. OSDU master-data
// Field, верхушка онтологии Field→Well→Wellbore. По паттерну CasingsPage
// (list + Editor в одном файле, refetch вместо оптимистики, list↔editor через
// search-параметры ?edit=N / ?new=1).
//
// Поля: name (обяз.), description/country/basin (текст, пусто = «не указано»),
// latitude_deg/longitude_deg (координаты центра — опциональны, пусто = NULL).

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listFields,
  createField,
  updateField,
  deleteField,
  InUseError,
} from './api/fields'
import type { FieldDto } from './api/fields'

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:        FieldDto | null
  name:          string
  description:   string
  country:       string
  basin:         string
  /// Координаты как строки — текстовое поле, парсим на submit. Пусто = не задано.
  latitude_deg:  string
  longitude_deg: string
}

function emptyEditor(): EditorState {
  return {
    source:        null,
    name:          '',
    description:   '',
    country:       '',
    basin:         '',
    latitude_deg:  '',
    longitude_deg: '',
  }
}

// `null` координата (не задана) → пустая строка в форме.
const coordStr = (v: number | null): string => (v == null ? '' : String(v))

function editorFrom(s: FieldDto): EditorState {
  return {
    source:        s,
    name:          s.name,
    description:   s.description,
    country:       s.country,
    basin:         s.basin,
    latitude_deg:  coordStr(s.latitude_deg),
    longitude_deg: coordStr(s.longitude_deg),
  }
}

type EditorProps = {
  state:   EditorState
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.description.trim().length > 0 ||
        c.country.trim().length > 0 ||
        c.basin.trim().length > 0 ||
        c.latitude_deg.trim().length > 0 ||
        c.longitude_deg.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.description !== initial.description
      || c.country !== initial.country
      || c.basin !== initial.basin
      || c.latitude_deg !== initial.latitude_deg
      || c.longitude_deg !== initial.longitude_deg
    )
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // Опциональная координата: пусто → null; иначе число в диапазоне [lo, hi].
  // Возвращает `number | null` при успехе, либо строку-сообщение об ошибке.
  const parseCoord = (raw: string, label: string, lo: number, hi: number): number | null | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    if (n < lo || n > hi) return `Поле ${label}: вне диапазона [${lo}, ${hi}]`
    return n
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    const lat = parseCoord(cur.latitude_deg,  'широта',  -90,  90)
    if (typeof lat === 'string') { setErr(lat); return }
    const lon = parseCoord(cur.longitude_deg, 'долгота', -180, 180)
    if (typeof lon === 'string') { setErr(lon); return }

    setBusy(true)
    try {
      const payload = {
        name:          nameTrimmed,
        description:   cur.description,
        country:       cur.country,
        basin:         cur.basin,
        latitude_deg:  lat,
        longitude_deg: lon,
      }
      if (isCreate()) {
        await createField(payload)
      } else {
        await updateField(cur.source!.id, payload)
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новое месторождение' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать месторождение' : st().name || 'Месторождение'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Самотлорское"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>
          </div>

          <div class="field">
            <div class="field-label">Геология и расположение</div>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Страна</span>
                  <span class="dim-hint">необязательно</span>
                </div>
                <input
                  class="field-input"
                  value={st().country}
                  onInput={(e) => setSt({ ...st(), country: e.currentTarget.value })}
                  placeholder="Россия"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Бассейн</span>
                  <span class="dim-hint">необязательно</span>
                </div>
                <input
                  class="field-input"
                  value={st().basin}
                  onInput={(e) => setSt({ ...st(), basin: e.currentTarget.value })}
                  placeholder="Западно-Сибирский"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Широта, °</span>
                  <span class="dim-hint">центр, [-90; 90]</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().latitude_deg}
                  onInput={(e) => setSt({ ...st(), latitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="61.05"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Долгота, °</span>
                  <span class="dim-hint">центр, [-180; 180]</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().longitude_deg}
                  onInput={(e) => setSt({ ...st(), longitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="76.8"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type FieldsPageProps = {
  fieldId?: number | null
  create?:  boolean
  onOpen:   (id: number) => void
  onCreate: () => void
  onBack:   () => void
}

export const FieldsPage: Component<FieldsPageProps> = (props) => {
  const [items, { refetch }] = createResource(listFields)
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.fieldId == null) return null
    const item = (items() ?? []).find((f) => f.id === props.fieldId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<FieldDto[]>(() => {
    const all = items() ?? []
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    return all.filter((f) =>
      f.name.toLowerCase().includes(q)
      || (f.description ?? '').toLowerCase().includes(q)
      || (f.country ?? '').toLowerCase().includes(q)
      || (f.basin ?? '').toLowerCase().includes(q)
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (f: FieldDto) => props.onOpen(f.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (f: FieldDto) => {
    if (!confirm(`Удалить «${f.name}»?`)) return
    setDelErr(null)
    try {
      await deleteField(f.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  // Краткая гео-сводка: страна · бассейн, либо «—».
  const geoSummary = (f: FieldDto): string => {
    const parts = [f.country, f.basin].map((s) => s.trim()).filter((s) => s.length > 0)
    return parts.length > 0 ? parts.join(' · ') : '—'
  }

  const coordsSummary = (f: FieldDto): string =>
    f.latitude_deg == null && f.longitude_deg == null
      ? '—'
      : `${f.latitude_deg ?? '?'}, ${f.longitude_deg ?? '?'}`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог месторождений</h1>
          <p class="page-subtitle">
            Месторождения (OSDU Field) — верхний уровень онтологии
            Field→Well→Wellbore. Страна, бассейн и точка центра.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Месторождение
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первое месторождение
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить месторождение
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / стране / бассейну"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.length}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Страна / бассейн</th>
                <th>Координаты</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(f) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(f)}>
                    <td class="mono text-xs muted">#{f.id}</td>
                    <td style={{ 'font-weight': 500 }}>{f.name}</td>
                    <td class="text-xs">{geoSummary(f)}</td>
                    <td class="mono text-xs">{coordsSummary(f)}</td>
                    <td class="text-xs muted">{f.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(f)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
